// src/pages/system/AccessPoints.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Alert,
  MenuItem,
  Grid,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  SignalCellularAlt as SignalIcon,
  People as PeopleIcon,
  RestartAlt as RestartIcon
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import CoordinateSelector from '../../components/maps/CoordinateSelector';

const APForm = ({ open, onClose, onSubmit, ap = null, zones = [], routers = [], nodes = [] }) => {
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    ip: '',
    username: '',
    password: '',
    model: '',
    antenna_type: '',
    zone_id: '',
    router_id: '',
    ssid: '',
    frequency: '',
    node_id: '',
    ip_subnet: '',
    router_interface: '',
    coordinates: '',
    comments: '',
    azimuth: 0,
    beamwidth: 90,
    coverage_radius: 500,
    ...ap
  });

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Actualizar el formulario cuando cambian los datos del AP
  useEffect(() => {
    if (ap) {
      setFormData({
        name: ap.name || '',
        type: ap.type || '',
        ip: ap.ip || '',
        username: ap.username || '',
        password: ap.password || '',
        model: ap.model || '',
        antenna_type: ap.antenna_type || '',
        zone_id: ap.zone_id || '',
        router_id: ap.router_id || '',
        ssid: ap.ssid || '',
        frequency: ap.frequency || '',
        node_id: ap.node_id || '',
        ip_subnet: ap.ip_subnet || '',
        router_interface: ap.router_interface || '',
        coordinates: ap.coordinates || '',
        comments: ap.comments || '',
        azimuth: ap.azimuth !== undefined && ap.azimuth !== null ? ap.azimuth : 0,
        beamwidth: ap.beamwidth !== undefined && ap.beamwidth !== null ? ap.beamwidth : 90,
        coverage_radius: ap.coverage_radius !== undefined && ap.coverage_radius !== null ? ap.coverage_radius : 500
      });
    } else {
      // Reiniciar el formulario cuando se crea un nuevo AP
      setFormData({
        name: '',
        type: '',
        ip: '',
        username: '',
        password: '',
        model: '',
        antenna_type: '',
        zone_id: '',
        router_id: '',
        ssid: '',
        frequency: '',
        node_id: '',
        ip_subnet: '',
        router_interface: '',
        coordinates: '',
        comments: '',
        azimuth: 0,
        beamwidth: 90,
        coverage_radius: 500
      });
    }
  }, [ap]);

  // Limpiar router_id y node_id si cambian de zona
  useEffect(() => {
    if (formData.zone_id) {
      if (formData.router_id) {
        const routerBelongsToZone = routers.some(r => r.id === formData.router_id && r.zone_id === formData.zone_id);
        if (!routerBelongsToZone) {
          setFormData(prev => ({ ...prev, router_id: '' }));
        }
      }
      if (formData.node_id) {
        const nodeBelongsToZone = nodes.some(n => n.id === formData.node_id && n.zone_id === formData.zone_id);
        if (!nodeBelongsToZone) {
          setFormData(prev => ({ ...prev, node_id: '' }));
        }
      }
    } else {
      setFormData(prev => ({ ...prev, router_id: '', node_id: '' }));
    }
  }, [formData.zone_id, routers, nodes]);

  // Limpiar node_id si cambia de router y el nodo no pertenece al nuevo router ni es general (sin router asignado)
  useEffect(() => {
    if (formData.router_id && formData.node_id) {
      const selectedNode = nodes.find(n => n.id === formData.node_id);
      if (selectedNode && selectedNode.router_id && selectedNode.router_id !== formData.router_id) {
        setFormData(prev => ({ ...prev, node_id: '' }));
      }
    }
  }, [formData.router_id, nodes]);

  const filteredRouters = routers.filter(router => router.zone_id === formData.zone_id);
  const filteredNodes = nodes.filter(node => {
    if (!formData.zone_id) return false;
    if (node.zone_id !== formData.zone_id) return false;
    if (formData.router_id) {
      return node.router_id === formData.router_id || !node.router_id;
    }
    return true;
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    // Validaciones básicas
    if (!formData.name || !formData.zone_id) {
      setError('Los campos nombre y zona son obligatorios');
      return;
    }

    setLoading(true);
    onSubmit(formData)
      .catch(error => {
        setError('Error al guardar: ' + error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const deviceTypes = [
    { value: 'sectorial', label: 'AP Sectorial' },
    { value: 'omnidireccional', label: 'AP Omnidireccional' },
    { value: 'ptp', label: 'Enlace PTP (Backhaul)' },
    { value: 'repetidor', label: 'Repetidor' }
  ];

  const frequencies = [
    '2.4 GHz',
    '5 GHz',
    '5.8 GHz',
    'Dual Band'
  ];

  const antennaTypes = [
    'Sectorial',
    'Omnidireccional',
    'Panel',
    'Dish',
    'Otro'
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {ap ? 'Editar AP / Sectorial' : 'Nuevo AP / Sectorial'}
        </DialogTitle>
        <DialogContent>
          <Box className="space-y-4 py-4">
            {error && (
              <Alert severity="error" className="mb-4">
                {error}
              </Alert>
            )}

            <Grid container spacing={2}>
              {/* ── SECCIÓN: Ubicación en la Red ── */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main', borderBottom: '2px solid', borderColor: 'primary.main', pb: 0.5, mt: 1, textTransform: 'uppercase', letterSpacing: 1 }}>
                  📍 Ubicación en la Red
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  select
                  label="Zona"
                  name="zone_id"
                  value={formData.zone_id}
                  onChange={handleChange}
                  required
                  size="small"
                >
                  {zones.map(zone => (
                    <MenuItem key={zone.id} value={zone.id}>
                      {zone.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  select
                  label="Router Administrador"
                  name="router_id"
                  value={formData.router_id || ''}
                  onChange={handleChange}
                  disabled={!formData.zone_id}
                  helperText={!formData.zone_id ? "Seleccione una Zona primero" : ""}
                  size="small"
                >
                  <MenuItem value=""><em>Sin asignar</em></MenuItem>
                  {filteredRouters.map(router => (
                    <MenuItem key={router.id} value={router.id}>
                      {router.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  select
                  label="Torre / Nodo"
                  name="node_id"
                  value={formData.node_id || ''}
                  onChange={handleChange}
                  required
                  disabled={!formData.zone_id}
                  helperText={!formData.zone_id ? "Seleccione una Zona primero" : (formData.router_id ? "Nodos del router seleccionado" : "Todos los nodos de la zona")}
                  size="small"
                >
                  <MenuItem value=""><em>Sin asignar</em></MenuItem>
                  {filteredNodes.map(node => (
                    <MenuItem key={node.id} value={node.id}>
                      {node.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* ── SECCIÓN: Información del Dispositivo ── */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main', borderBottom: '2px solid', borderColor: 'primary.main', pb: 0.5, mt: 2, textTransform: 'uppercase', letterSpacing: 1 }}>
                  📡 Información del Dispositivo
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Nombre del AP"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  size="small"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  select
                  label="Tipo de Dispositivo"
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  required
                  size="small"
                >
                  {deviceTypes.map(type => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Modelo / Marca"
                  name="model"
                  value={formData.model}
                  onChange={handleChange}
                  placeholder="Ej: Ubiquiti LiteBeam 5AC"
                  size="small"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Dirección IP"
                  name="ip"
                  value={formData.ip}
                  onChange={handleChange}
                  placeholder="192.168.1.100"
                  size="small"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Usuario de acceso"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder="admin"
                  size="small"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Contraseña"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>

              {/* ── SECCIÓN: Configuración Inalámbrica ── */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main', borderBottom: '2px solid', borderColor: 'primary.main', pb: 0.5, mt: 2, textTransform: 'uppercase', letterSpacing: 1 }}>
                  📶 Configuración Inalámbrica
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="SSID"
                  name="ssid"
                  value={formData.ssid}
                  onChange={handleChange}
                  placeholder="Nombre de la red WiFi"
                  size="small"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  select
                  label="Frecuencia"
                  name="frequency"
                  value={formData.frequency}
                  onChange={handleChange}
                  size="small"
                >
                  {frequencies.map(freq => (
                    <MenuItem key={freq} value={freq}>
                      {freq}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  select
                  label="Tipo de Antena"
                  name="antenna_type"
                  value={formData.antenna_type}
                  onChange={handleChange}
                  size="small"
                >
                  {antennaTypes.map(type => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* ── SECCIÓN: Configuración de Red ── */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main', borderBottom: '2px solid', borderColor: 'primary.main', pb: 0.5, mt: 2, textTransform: 'uppercase', letterSpacing: 1 }}>
                  🌐 Configuración de Red
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Subred del AP (opcional)"
                  name="ip_subnet"
                  value={formData.ip_subnet}
                  onChange={handleChange}
                  placeholder="192.168.100.0/24"
                  helperText="Dejar vacío si comparte la subred del Router"
                  size="small"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Interfaz en Router (opcional)"
                  name="router_interface"
                  value={formData.router_interface}
                  onChange={handleChange}
                  placeholder="ether3"
                  helperText="Puerto del Router al que conecta este AP"
                  size="small"
                />
              </Grid>

              {/* ── SECCIÓN: Cobertura y Ubicación ── */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main', borderBottom: '2px solid', borderColor: 'primary.main', pb: 0.5, mt: 2, textTransform: 'uppercase', letterSpacing: 1 }}>
                  🗺️ Cobertura y Ubicación
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <CoordinateSelector
                  value={formData.coordinates}
                  onChange={(newCoordinates) => handleChange({
                    target: { name: 'coordinates', value: newCoordinates }
                  })}
                  label="Coordenadas"
                  helperText="Formato: latitud, longitud"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Radio de Cobertura (metros)"
                  name="coverage_radius"
                  value={formData.coverage_radius}
                  onChange={handleChange}
                  inputProps={{ min: 50, max: 20000 }}
                  helperText="Ej: 500 a 10000 metros"
                  size="small"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Rumbo / Azimut (grados)"
                  name="azimuth"
                  value={formData.azimuth}
                  onChange={handleChange}
                  inputProps={{ min: 0, max: 360 }}
                  helperText="Dirección a la que apunta: 0° = Norte, 90° = Este"
                  size="small"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Apertura del Haz (grados)"
                  name="beamwidth"
                  value={formData.beamwidth}
                  onChange={handleChange}
                  inputProps={{ min: 10, max: 360 }}
                  helperText="Sectorial: 60°-120°, Omni: 360°"
                  size="small"
                />
              </Grid>

              {/* ── SECCIÓN: Notas ── */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main', borderBottom: '2px solid', borderColor: 'primary.main', pb: 0.5, mt: 2, textTransform: 'uppercase', letterSpacing: 1 }}>
                  📝 Notas
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Comentarios"
                  name="comments"
                  value={formData.comments}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  size="small"
                  placeholder="Observaciones adicionales sobre este equipo..."
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>Cancelar</Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const AccessPoints = () => {
  const [aps, setAPs] = useState([]);
  const [zones, setZones] = useState([]);
  const [routers, setRouters] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [selectedAP, setSelectedAP] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [apToDelete, setAPToDelete] = useState(null);
  const [rebootConfirmOpen, setRebootConfirmOpen] = useState(false);
  const [apToReboot, setAPToReboot] = useState(null);
  const [rebooting, setRebooting] = useState(false);
  const [successMsg, setSuccessMsg] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [apsRes, zonesRes, routersRes, nodesRes] = await Promise.all([
        fetch('/api/access-points', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch('/api/zones', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch('/api/routers', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch('/api/nodes', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        })
      ]);

      if (!apsRes.ok || !zonesRes.ok || !routersRes.ok || !nodesRes.ok) {
        throw new Error('Error al cargar datos iniciales');
      }

      const [apsData, zonesData, routersData, nodesData] = await Promise.all([
        apsRes.json(),
        zonesRes.json(),
        routersRes.json(),
        nodesRes.json()
      ]);

      setAPs(apsData);
      setZones(zonesData);
      setRouters(routersData);
      setNodes(nodesData);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Error al cargar datos: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (formData) => {
    try {
      const method = selectedAP ? 'PUT' : 'POST';
      const url = selectedAP
        ? `/api/access-points/${selectedAP.id}`
        : '/api/access-points';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al guardar el AP / Sectorial');
      }

      await fetchInitialData();
      setOpenForm(false);
      setSelectedAP(null);
    } catch (error) {
      console.error('Error saving AP:', error);
      throw error;
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetch(`/api/access-points/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al eliminar el AP / Sectorial');
      }

      await fetchInitialData();
      setDeleteConfirmOpen(false);
      setAPToDelete(null);
    } catch (error) {
      console.error('Error deleting AP:', error);
      setError('Error al eliminar AP / Sectorial: ' + error.message);
    }
  };

  const handleRebootAP = async () => {
    if (!apToReboot) return;
    setRebooting(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const response = await fetch(`/api/access-points/${apToReboot.id}/reboot`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Error al enviar comando de reinicio al AP');
      }
      setSuccessMsg(data.message);
      setRebootConfirmOpen(false);
      setAPToReboot(null);
    } catch (err) {
      console.error('Error rebooting AP:', err);
      setError(err.message);
    } finally {
      setRebooting(false);
    }
  };

  const columns = [
    { field: 'name', headerName: 'Nombre', width: 180 },
    { 
      field: 'type', 
      headerName: 'Tipo', 
      width: 150,
      renderCell: (params) => {
        const typeLabels = {
          sectorial: 'AP Sectorial',
          omnidireccional: 'AP Omnidireccional',
          ptp: 'Enlace PTP (Backhaul)',
          repetidor: 'Repetidor'
        };
        return typeLabels[params.value] || params.value;
      }
    },
    { field: 'ip', headerName: 'IP', width: 130 },
    {
      field: 'zone_name',
      headerName: 'Zona',
      width: 150,
    },
    {
      field: 'router_name',
      headerName: 'Router Admin',
      width: 160,
      renderCell: (params) => params.value || 'Sin asignar'
    },
    {
      field: 'node_name',
      headerName: 'Torre / Nodo',
      width: 160,
      renderCell: (params) => params.value || 'Sin asignar'
    },
    {
      field: 'ip_subnet',
      headerName: 'Subred Propia',
      width: 150,
      renderCell: (params) => params.value || 'Compartida'
    },
    {
      field: 'router_interface',
      headerName: 'Interfaz AP',
      width: 130,
      renderCell: (params) => params.value || 'Default'
    },
    { field: 'model', headerName: 'Modelo', width: 150 },
    { field: 'frequency', headerName: 'Frecuencia', width: 120 },
    { field: 'coverage_radius', headerName: 'Radio Cobertura (m)', width: 150, type: 'number' },
    { field: 'azimuth', headerName: 'Azimut (Rumbo)', width: 130, type: 'number', renderCell: (params) => `${params.value !== null && params.value !== undefined ? params.value : 0}°` },
    { field: 'beamwidth', headerName: 'Apertura Haz', width: 120, type: 'number', renderCell: (params) => `${params.value !== null && params.value !== undefined ? params.value : 90}°` },
    {
      field: 'client_count',
      headerName: 'Clientes',
      width: 100,
      type: 'number',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PeopleIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
          {params.value || 0}
        </Box>
      )
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 170,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Editar AP">
            <IconButton
              size="small"
              onClick={async () => {
                try {
                  const response = await fetch(`/api/access-points/${params.row.id}`, {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                  });

                  if (!response.ok) {
                    throw new Error('Error al obtener datos del AP / Sectorial');
                  }

                  const apData = await response.json();
                  setSelectedAP(apData);
                  setOpenForm(true);
                } catch (error) {
                  console.error('Error loading AP data:', error);
                  setError('Error al cargar datos del AP / Sectorial: ' + error.message);
                }
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Reiniciar AP">
            <IconButton
              size="small"
              color="warning"
              onClick={() => {
                setAPToReboot(params.row);
                setRebootConfirmOpen(true);
              }}
            >
              <RestartIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Eliminar AP">
            <IconButton
              size="small"
              color="error"
              onClick={() => {
                setAPToDelete(params.row);
                setDeleteConfirmOpen(true);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ backgroundColor: '#f8fafc', minHeight: '100vh', p: { xs: 1.5, sm: 3 }, py: { xs: 2, sm: 4 } }}>
      {/* Cabecera Premium */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, mb: 4, gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px', fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
            Puntos de Acceso / Sectoriales
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5, fontWeight: 500 }}>
            Configure y supervise las antenas AP y paneles sectoriales de transmisión.
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => {
            setSelectedAP(null);
            setOpenForm(true);
          }}
          sx={{
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 700,
            px: 3,
            py: 1.2,
            backgroundColor: '#6366f1',
            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.2)',
            '&:hover': { backgroundColor: '#4f46e5' },
            width: { xs: '100%', sm: 'auto' }
          }}
        >
          Nuevo AP / Sectorial
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: '16px' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMsg && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: '16px' }} onClose={() => setSuccessMsg(null)}>
          {successMsg}
        </Alert>
      )}

      {/* Tabla Premium */}
      <Paper
        sx={{
          borderRadius: '24px',
          boxShadow: '0 4px 30px rgba(0,0,0,0.02)',
          border: '1px solid rgba(0,0,0,0.05)',
          overflow: 'hidden',
          backgroundColor: 'white',
          p: 1
        }}
      >
        <DataGrid
          rows={aps}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          disableSelectionOnClick
          loading={loading}
          autoHeight
          sx={{
            border: 'none',
            fontFamily: 'Inter, sans-serif',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f8fafc',
              borderBottom: '1px solid rgba(0,0,0,0.06)',
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 700,
                color: '#475569',
                fontSize: '0.85rem'
              }
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid rgba(0,0,0,0.04)',
              color: '#334155',
              fontSize: '0.875rem'
            },
            '& .MuiDataGrid-row': {
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: '#f1f5f9',
                transform: 'scale(1.002)',
              }
            },
            '& .MuiDataGrid-row.Mui-selected': {
              backgroundColor: 'rgba(99, 102, 241, 0.08)',
              '&:hover': {
                backgroundColor: 'rgba(99, 102, 241, 0.12)',
              }
            },
          }}
        />
      </Paper>

      <APForm
        open={openForm}
        onClose={() => {
          setOpenForm(false);
          setSelectedAP(null);
        }}
        onSubmit={handleSubmit}
        ap={selectedAP}
        zones={zones}
        routers={routers}
        nodes={nodes}
      />

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          ¿Estás seguro de que deseas eliminar el AP / Sectorial "{apToDelete?.name}"?
          {apToDelete?.client_count > 0 && (
            <Typography color="error" className="mt-2">
              ¡Advertencia! Este AP / Sectorial tiene {apToDelete.client_count} clientes asignados.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>
            Cancelar
          </Button>
          <Button
            onClick={() => handleDelete(apToDelete.id)}
            color="error"
            variant="contained"
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Confirmación de Reinicio de AP */}
      <Dialog
        open={rebootConfirmOpen}
        onClose={() => !rebooting && setRebootConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: '#d97706', display: 'flex', alignItems: 'center', gap: 1 }}>
          <RestartIcon /> Reiniciar AP / Sectorial
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 1 }}>
            ¿Está seguro de reiniciar el equipo <strong>"{apToReboot?.name}"</strong> ({apToReboot?.ip})?
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, bgcolor: '#fffbeb', p: 1.5, borderRadius: '10px', border: '1px solid #fde68a', color: '#92400e' }}>
            ⚠️ <strong>Atención:</strong> Los clientes conectados a este AP perderán señal inalámbrica temporalmente durante 1 a 2 minutos hasta que el equipo se reinicie por completo.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button onClick={() => setRebootConfirmOpen(false)} disabled={rebooting} sx={{ borderRadius: '8px' }}>
            Cancelar
          </Button>
          <Button
            onClick={handleRebootAP}
            color="warning"
            variant="contained"
            disabled={rebooting}
            startIcon={rebooting ? <CircularProgress size={18} color="inherit" /> : <RestartIcon />}
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700 }}
          >
            {rebooting ? 'Enviando Reinicio...' : 'Confirmar Reinicio'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccessPoints;